import { getAccessToken, getScopeHeaders } from '../lib/auth-storage';

const API_BASE = import.meta.env.VITE_PRODUCT_STRUCTURE_API ?? '/api/v1';

export type RelationshipType =
  | 'cross_sell'
  | 'up_sell'
  | 'frequently_bought_together'
  | 'similar'
  | 'accessory'
  | 'dependency'
  | 'variant'
  | 'bundle_component'
  | 'replacement'
  | 'related';

export type BundleType = 'bundle' | 'kit' | 'multipack' | 'composite';
export type VariantDimensionType = 'color' | 'size' | 'material' | 'style' | 'custom';

export type VariantOption = { id?: string; dimension_id?: string; value: string; label?: string; sort_order?: number };
export type VariantDimension = { id?: string; name: string; type: VariantDimensionType; options?: VariantOption[] };

export type VariantGroup = {
  id: string;
  tenant_id: string;
  organization_id: string;
  product_id: string;
  name: string;
  code?: string;
  status: string;
  dimensions?: VariantDimension[];
  variants?: Variant[];
  metadata?: Record<string, string>;
  version: number;
  created_at: string;
  updated_at: string;
};

export type Variant = {
  id: string;
  variant_group_id: string;
  product_id?: string;
  sku?: string;
  name?: string;
  options?: Record<string, string>;
  is_default?: boolean;
  created_at: string;
  updated_at: string;
};

export type VariantCombination = {
  id: string;
  variant_group_id: string;
  sku?: string;
  product_id?: string;
  options: Record<string, string>;
  created_at: string;
};

export type VariantMatrix = {
  variant_group_id: string;
  dimensions: VariantDimension[];
  combinations: VariantCombination[];
};

export type BundleItem = { id?: string; product_id: string; quantity: number; required?: boolean; sort_order?: number };
export type BundleRule = { id?: string; rule_type: string; expression: string; priority?: number };

export type Bundle = {
  id: string;
  tenant_id: string;
  organization_id: string;
  name: string;
  code?: string;
  type: BundleType;
  status: string;
  items?: BundleItem[];
  rules?: BundleRule[];
  metadata?: Record<string, string>;
  version: number;
  created_at: string;
  updated_at: string;
};

export type ProductRelationship = {
  id: string;
  tenant_id: string;
  organization_id: string;
  source_product_id: string;
  target_product_id: string;
  type: RelationshipType;
  status: string;
  priority?: number;
  metadata?: Record<string, string>;
  created_at: string;
  updated_at: string;
};

export type ProductFamily = {
  id: string;
  tenant_id: string;
  organization_id: string;
  name: string;
  code?: string;
  description?: string;
  product_ids?: string[];
  metadata?: Record<string, string>;
  created_at: string;
  updated_at: string;
};

export type MarketplaceRelationship = {
  id: string;
  tenant_id: string;
  organization_id: string;
  product_id: string;
  marketplace: string;
  external_id?: string;
  type: RelationshipType;
  target_product_id?: string;
  metadata?: Record<string, string>;
  created_at: string;
  updated_at: string;
};

export type StructureDashboard = {
  stats?: Record<string, unknown>;
  widgets?: Array<{ id: string; title: string; type: string; order?: number }>;
};

function headers(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...getScopeHeaders(),
    ...(getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {}),
  };
}

async function psFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { ...headers(), ...(init?.headers as Record<string, string> | undefined) },
  });
  if (!res.ok) throw new Error(`ProductStructure ${path} failed: ${res.status} ${await res.text()}`);
  const body = await res.json();
  return (body.data ?? body) as T;
}

export const productStructureApi = {
  dashboard: () => psFetch<StructureDashboard>('/product-structure/dashboard'),

  listVariants: (params?: { product_id?: string; status?: string }) => {
    const qs = new URLSearchParams();
    if (params?.product_id) qs.set('product_id', params.product_id);
    if (params?.status) qs.set('status', params.status);
    const q = qs.toString();
    return psFetch<VariantGroup[]>(`/variants${q ? `?${q}` : ''}`);
  },
  getVariant: (id: string) => psFetch<VariantGroup>(`/variants/${id}`),
  createVariant: (input: {
    product_id: string;
    name: string;
    code?: string;
    status?: string;
    dimensions?: VariantDimension[];
    metadata?: Record<string, string>;
  }) => psFetch<VariantGroup>('/variants', { method: 'POST', body: JSON.stringify(input) }),
  updateVariant: (id: string, input: Partial<VariantGroup>) =>
    psFetch<VariantGroup>(`/variants/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
  deleteVariant: (id: string) => psFetch<void>(`/variants/${id}`, { method: 'DELETE' }),
  generateVariants: (input: { variant_group_id: string; sku_prefix?: string }) =>
    psFetch<VariantMatrix>('/variants/generate', { method: 'POST', body: JSON.stringify(input) }),

  listBundles: (params?: { type?: BundleType; status?: string }) => {
    const qs = new URLSearchParams();
    if (params?.type) qs.set('type', params.type);
    if (params?.status) qs.set('status', params.status);
    const q = qs.toString();
    return psFetch<Bundle[]>(`/bundles${q ? `?${q}` : ''}`);
  },
  getBundle: (id: string) => psFetch<Bundle>(`/bundles/${id}`),
  createBundle: (input: {
    name: string;
    code?: string;
    type?: BundleType;
    items?: BundleItem[];
    rules?: BundleRule[];
    metadata?: Record<string, string>;
  }) => psFetch<Bundle>('/bundles', { method: 'POST', body: JSON.stringify(input) }),
  updateBundle: (id: string, input: Partial<Bundle>) =>
    psFetch<Bundle>(`/bundles/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
  deleteBundle: (id: string) => psFetch<void>(`/bundles/${id}`, { method: 'DELETE' }),

  listRelationships: (params?: { source_product_id?: string; target_product_id?: string; type?: RelationshipType }) => {
    const qs = new URLSearchParams();
    if (params?.source_product_id) qs.set('source_product_id', params.source_product_id);
    if (params?.target_product_id) qs.set('target_product_id', params.target_product_id);
    if (params?.type) qs.set('type', params.type);
    const q = qs.toString();
    return psFetch<ProductRelationship[]>(`/product-relationships${q ? `?${q}` : ''}`);
  },
  createRelationship: (input: {
    source_product_id: string;
    target_product_id: string;
    type: RelationshipType;
    status?: string;
    priority?: number;
    metadata?: Record<string, string>;
  }) => psFetch<ProductRelationship>('/product-relationships', { method: 'POST', body: JSON.stringify(input) }),
  updateRelationship: (id: string, input: Partial<ProductRelationship>) =>
    psFetch<ProductRelationship>(`/product-relationships/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
  deleteRelationship: (id: string) => psFetch<void>(`/product-relationships/${id}`, { method: 'DELETE' }),

  listFamilies: () => psFetch<ProductFamily[]>('/product-families'),
  createFamily: (input: { name: string; code?: string; description?: string; product_ids?: string[] }) =>
    psFetch<ProductFamily>('/product-families', { method: 'POST', body: JSON.stringify(input) }),

  listMarketplaceRelationships: (params?: { product_id?: string; marketplace?: string }) => {
    const qs = new URLSearchParams();
    if (params?.product_id) qs.set('product_id', params.product_id);
    if (params?.marketplace) qs.set('marketplace', params.marketplace);
    const q = qs.toString();
    return psFetch<MarketplaceRelationship[]>(`/marketplace-relationships${q ? `?${q}` : ''}`);
  },
  createMarketplaceRelationship: (input: {
    product_id: string;
    marketplace: string;
    type: RelationshipType;
    external_id?: string;
    target_product_id?: string;
  }) => psFetch<MarketplaceRelationship>('/marketplace-relationships', { method: 'POST', body: JSON.stringify(input) }),
};
